import { useState } from "react"
import type { ReactElement } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { SupervisorAccountOutlined } from "@mui/icons-material"
import { Grid } from "@mui/material"
import { Tab, Tabs } from "@synapse-analytics/synapse-ui"

import { AccountAndOrganizationIcon } from "../assets/SvgIcons"
import { AccountAndOrganization, RolesAndPermissions } from "../features/Settings"
import type { RootState } from "../store/ReduxStore"
import { useIsPermitted } from "../utils/PermissionsHelpers"

export function Settings(): ReactElement {
  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const flattenedSidPermissions = useSelector((state: RootState) => state.permissions.flattenedSidPermissions)

  const { t } = useTranslation("settings")

  // Permission check constants
  const canListRoles = useIsPermitted("List roles", permissions.synapse_id, flattenedSidPermissions)
  const canListUsers = useIsPermitted("List users", permissions.synapse_id, flattenedSidPermissions)
  const canListGroups = useIsPermitted("List groups", permissions.synapse_id, flattenedSidPermissions)
  const isRolesPermitted = canListRoles || canListUsers || canListGroups

  const [tab, setTab] = useState<"permissions" | "account">("permissions")

  if (!isRolesPermitted)
    return (
      <Grid container gap={2} p={2}>
        <AccountAndOrganization />
      </Grid>
    )

  return (
    <Grid container gap={2} p={2}>
      <Tabs value={tab}>
        <Tab
          icon={() => <SupervisorAccountOutlined fontSize="small" />}
          label={t("tabs.accessControl")}
          value="permissions"
          selected={tab === "permissions"}
          onClick={() => setTab("permissions")}
          size="large"
        />

        <Tab
          icon={() => <AccountAndOrganizationIcon height="20" width="20" />}
          label={t("tabs.accountAndOrganization")}
          value="account"
          selected={tab === "account"}
          onClick={() => setTab("account")}
          size="large"
        />
      </Tabs>

      {tab === "permissions" ? <RolesAndPermissions /> : <AccountAndOrganization />}
    </Grid>
  )
}
